import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { ApiError } from '../../lib/api';
import { parseHistoryMessages, type HistoryItem, type HistoryMessage } from './history';

function agentBase() {
  let configured = '';
  try { configured = window.localStorage.getItem('caigen.agent.api') || ''; } catch { /* storage may be disabled */ }
  if (configured) return configured.replace(/\/$/, '');
  if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `${window.location.protocol}//${window.location.hostname}:8790`;
  }
  return `${window.location.origin}/agentapi`;
}

interface AgentConversationProps {
  initialPrompt?: string;
  surface?: string;
  spacious?: boolean;
}

type ToolState = 'running' | 'waiting' | 'ok' | 'error';
interface ToolItem {
  id: string;
  tool: string;
  args: Record<string, unknown>;
  state: ToolState;
  summary?: string;
  duration?: number;
}
interface Confirmation {
  id: string;
  tool: string;
  args: Record<string, unknown>;
  reason: string;
  decision?: 'accepted' | 'rejected';
}
interface RenderView {
  id: string;
  app: string;
  view: { kind?: string; data?: unknown };
}
interface PlanStep { tool?: string; label?: string }
interface ProgressItem { id: number; stage: string; label: string }
interface AgentSession { id: string; title?: string; created_at?: string; updated_at?: string; message_count?: number }

const SESSION_STORAGE_KEY = 'caigen.agent.session:v1';

function storedSessionId() {
  try { return window.localStorage.getItem(SESSION_STORAGE_KEY) || ''; } catch { return ''; }
}

function rememberSession(id?: string) {
  try {
    if (id) window.localStorage.setItem(SESSION_STORAGE_KEY, id);
    else window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch { /* session still works without browser persistence */ }
}

function sessionTime(value?: string) {
  if (!value) return '刚刚';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

function sessionTitle(value?: string) {
  return (value || '').replace(/^#+\s*/, '').replace(/\s+/g, ' ').trim() || '未命名会话';
}

const SAFE_AGENT_FAILURE = '本轮回答未能完成。内部错误详情已隐藏，请稍后重试；如果持续失败，请联系管理员检查 Agent 配置。';
const UNSAFE_AGENT_PATTERN = /(?:api[_-]?key|authorization|bearer\s+[a-z0-9._-]+|sk-[a-z0-9_-]{6,}|base[_-]?url|openai[_-]api|\/chat\/completions|upstream[^\n]{0,30}(?:error|failed))/i;

function safeAgentText(value: string) {
  return UNSAFE_AGENT_PATTERN.test(value) ? SAFE_AGENT_FAILURE : value;
}

const toolNames: Record<string, string> = {
  calendar_list: '读取日历', calendar_add: '写入日历', calendar_update: '更新日程', calendar_remove: '删除日程',
  wordess_stats: '学习统计', wordess_weak_words: '薄弱词', wordess_due_words: '待复习词', wordess_word: '词汇详情',
  whaleu_search: '校园信息检索', memory_read: '读取记忆', memory_write: '更新记忆', web_search: '联网检索',
  profile_read: '读取画像', profile_update: '更新画像',
};
const progressStages: Record<string, string> = {
  received: '接收', reasoning: '分析', tools: '工具', synthesis: '整理', answering: '回答',
};

function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={index}>{part.slice(2, -2)}</strong>
      : part,
  );
}

function RichText({ text }: { text: string }) {
  return <div className="agent-rich-text">{text.split('\n').map((line, index) => {
    const value = line.trim();
    if (!value) return <span className="agent-text-gap" key={index} />;
    if (value.startsWith('### ')) return <h4 key={index}>{inline(value.slice(4))}</h4>;
    if (/^[-•]\s+/.test(value)) return <p className="agent-bullet" key={index}>{inline(value.replace(/^[-•]\s+/, ''))}</p>;
    if (/^\d+[.)]\s+/.test(value)) return <p className="agent-number" key={index}>{inline(value)}</p>;
    return <p key={index}>{inline(value)}</p>;
  })}</div>;
}

function dataItems(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const object = data as Record<string, unknown>;
  for (const key of ['items', 'words', 'terms', 'events', 'todos']) {
    if (Array.isArray(object[key])) return object[key] as Record<string, unknown>[];
  }
  return [];
}

function RenderCard({ item }: { item: RenderView }) {
  const kind = item.view?.kind || 'data';
  const labels: Record<string, string> = {
    schedule_list: '日程与待办', notice_list: '校园信息', weak_list: '薄弱词', due_list: '今日复习',
    word_cards: '词汇卡片', term_list: '领域术语',
  };
  const items = dataItems(item.view?.data).slice(0, 6);
  return <section className={`agent-render-card agent-render-card--${item.app}`}>
    <header><span>{item.app === 'calendar' ? '历' : item.app === 'wordess' ? '词' : '证'}</span><div><small>{item.app.toUpperCase()}</small><b>{labels[kind] || kind}</b></div></header>
    {items.length ? <div className="agent-render-list">{items.map((entry, index) => <div key={String(entry.id || index)}>
      <span>{String(entry.time || entry.date || entry.due || '—')}</span>
      <p><b>{String(entry.title || entry.word || entry.term || entry.name || '未命名')}</b>{entry.summary ? <small>{String(entry.summary)}</small> : null}</p>
    </div>)}</div> : <p className="agent-render-empty">已读取结构化结果，当前没有可展示条目。</p>}
    {item.app === 'calendar' && <a href="/calendar">在日历中查看</a>}
  </section>;
}

export function AgentConversation({ initialPrompt, surface = 'shell', spacious = false }: AgentConversationProps) {
  const [input, setInput] = useState(initialPrompt || '');
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState('待命');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [plan, setPlan] = useState<PlanStep[]>([]);
  const [tools, setTools] = useState<ToolItem[]>([]);
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const [renders, setRenders] = useState<RenderView[]>([]);
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [sessionPanelOpen, setSessionPanelOpen] = useState(() => spacious && window.matchMedia('(min-width: 901px)').matches);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState('');
  const [confirmEmptyCleanup, setConfirmEmptyCleanup] = useState(false);
  const [deletingSessionIds, setDeletingSessionIds] = useState<string[]>([]);
  const [rewindingSeq, setRewindingSeq] = useState<number>();
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const sessionRef = useRef<string>();
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const eventsRef = useRef<EventSource>();
  const connectionRef = useRef<Promise<void>>();
  const replyQueueRef = useRef('');
  const rawReplyRef = useRef('');
  const unsafeReplyRef = useRef(false);
  const flushTimerRef = useRef<number>();
  const completionPendingRef = useRef(false);
  const eventNumber = useRef(0);
  const progressNumber = useRef(0);

  useEffect(() => { setInput(initialPrompt || ''); }, [initialPrompt]);
  useEffect(() => () => {
    eventsRef.current?.close();
    if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
  }, []);

  const finishIfDrained = () => {
    if (!completionPendingRef.current || replyQueueRef.current) return;
    completionPendingRef.current = false;
    setStatus('回答完成');
    setBusy(false);
  };

  const scheduleReplyFlush = () => {
    if (flushTimerRef.current) return;
    const flush = () => {
      flushTimerRef.current = undefined;
      const queued = replyQueueRef.current;
      if (!queued) { finishIfDrained(); return; }
      const size = Math.min(18, Math.max(1, Math.ceil(queued.length / 10)));
      const chunk = queued.slice(0, size);
      replyQueueRef.current = queued.slice(size);
      setReply((current) => current + chunk);
      if (replyQueueRef.current) flushTimerRef.current = window.setTimeout(flush, 24);
      else finishIfDrained();
    };
    flushTimerRef.current = window.setTimeout(flush, 0);
  };

  const addProgress = (stage: string, label: string) => {
    if (!label) return;
    setProgress((current) => current.at(-1)?.label === label ? current : [...current, {
      id: progressNumber.current++, stage, label,
    }].slice(-10));
  };

  const resetActivity = () => {
    setReply(''); setProgress([]); setPlan([]); setTools([]); setConfirmations([]); setRenders([]);
    replyQueueRef.current = '';
    rawReplyRef.current = '';
    unsafeReplyRef.current = false;
    completionPendingRef.current = false;
    if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
    flushTimerRef.current = undefined;
  };

  const connect = (sessionId: string) => {
    eventsRef.current?.close();
    const events = new EventSource(`${agentBase()}/api/agent/sessions/${sessionId}/events`, { withCredentials: true });
    const ready = new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          events.close();
          reject(new Error('Agent 事件流连接超时'));
        }
      }, 8000);
      events.onopen = () => {
        settled = true;
        window.clearTimeout(timeout);
        resolve();
      };
      events.onerror = () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeout);
          reject(new Error('Agent 事件流连接失败'));
          return;
        }
        setStatus((current) => current === '回答完成' ? current : 'Agent 事件流暂时中断，正在重连');
      };
    });
    events.onmessage = (event) => {
      if (!event.data) return;
      let message: Record<string, unknown>;
      try { message = JSON.parse(event.data); } catch { return; }
      const type = String(message.type || '');
      const tool = String(message.tool || '');
      const toolId = String(message.tool_id || '');
      if (type === 'text.delta') {
        const delta = String(message.text || '');
        rawReplyRef.current += delta;
        if (UNSAFE_AGENT_PATTERN.test(rawReplyRef.current)) {
          unsafeReplyRef.current = true;
          replyQueueRef.current = '';
          if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
          flushTimerRef.current = undefined;
          setReply(SAFE_AGENT_FAILURE);
          setStatus('回答失败，错误详情已隐藏');
        } else if (!unsafeReplyRef.current) {
          replyQueueRef.current += delta;
          scheduleReplyFlush();
        }
      }
      if (type === 'agent.progress') {
        addProgress(String(message.stage || 'reasoning'), String(message.label || '正在处理'));
        setStatus(String(message.label || '正在结合你的上下文思考'));
      }
      if (type === 'agent.thinking') addProgress('reasoning', '模型正在分析目标、约束和上下文');
      if (type === 'agent.plan') setPlan(Array.isArray(message.steps) ? message.steps as PlanStep[] : []);
      if (type === 'tool.call') {
        setStatus(`正在调用 ${toolNames[tool] || tool || '工具'}`);
        setTools((current) => current.some((item) => item.id === toolId) ? current.map((item) => item.id === toolId ? { ...item, state: message.awaiting ? 'waiting' : 'running' } : item) : [...current, {
          id: toolId, tool, args: (message.args || {}) as Record<string, unknown>, state: message.awaiting ? 'waiting' : 'running',
        }]);
      }
      if (type === 'tool.confirm') setConfirmations((current) => current.some((item) => item.id === toolId) ? current : [...current, {
        id: toolId, tool, args: (message.args || {}) as Record<string, unknown>, reason: String(message.reason || '写操作需要确认'),
      }]);
      if (type === 'tool.result') setTools((current) => current.map((item) => item.id === toolId ? {
        ...item, state: message.ok ? 'ok' : 'error', summary: String(message.summary || ''), duration: Number(message.duration_ms || 0),
      } : item));
      if (type === 'agent.render') setRenders((current) => [...current, {
        id: `${String(message.run_id || 'run')}:${eventNumber.current++}`,
        app: String(message.app || 'data'),
        view: (message.view || {}) as RenderView['view'],
      }]);
      if (type === 'agent.completed') { completionPendingRef.current = true; scheduleReplyFlush(); }
      if (type === 'agent.error') { setReply(SAFE_AGENT_FAILURE); setStatus('回答失败，错误详情已隐藏'); setBusy(false); }
    };
    eventsRef.current = events;
    connectionRef.current = ready;
    return ready;
  };

  const loadHistory = async (sessionId: string) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionId}/messages?limit=200`, { credentials: 'include' });
      if (!response.ok) throw new Error(`会话历史加载失败（${response.status}）`);
      const payload = await response.json();
      const messages = parseHistoryMessages(payload.messages);
      setHistory(messages);
      setStatus(messages.length ? '可继续对话' : '新会话');
    } catch (error) {
      setHistory([]);
      setStatus(error instanceof Error ? error.message : '会话历史加载失败');
    } finally { setHistoryLoading(false); }
  };

  const activateSession = async (session: AgentSession) => {
    if (busy || session.id === sessionRef.current) { setSessionPanelOpen(spacious && window.innerWidth > 900); return; }
    resetActivity();
    sessionRef.current = session.id;
    setActiveSessionId(session.id);
    rememberSession(session.id);
    setSessionPanelOpen(spacious && window.innerWidth > 900);
    await Promise.allSettled([loadHistory(session.id), connect(session.id)]);
  };

  useEffect(() => {
    let active = true;
    void fetch(`${agentBase()}/api/agent/sessions`, { credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 401 ? '请先登录菜根账户' : `会话列表加载失败（${response.status}）`);
        const payload = await response.json();
        if (!active) return;
        const items = (Array.isArray(payload.sessions) ? payload.sessions : []) as AgentSession[];
        setSessions(items);
        const remembered = storedSessionId();
        const selected = items.find((item) => item.id === remembered);
        if (selected) await activateSession(selected);
      })
      .catch((error) => { if (active) setStatus(error instanceof Error ? error.message : 'Agent 服务不可用'); });
    return () => { active = false; };
  // Only restore once; session switching is user-driven after mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newSession = () => {
    if (busy) return;
    eventsRef.current?.close();
    sessionRef.current = undefined;
    setActiveSessionId('');
    setHistory([]);
    setInput(initialPrompt || '');
    setStatus('新会话');
    setSessionPanelOpen(spacious && window.innerWidth > 900);
    setDeleteCandidateId('');
    setConfirmEmptyCleanup(false);
    rememberSession();
    resetActivity();
  };

  const deleteSessions = async (ids: string[]) => {
    const targets = [...new Set(ids)].filter(Boolean);
    if (!targets.length || busy) return;
    setDeletingSessionIds(targets);
    const activeDeleted = targets.includes(sessionRef.current || '');
    if (activeDeleted) eventsRef.current?.close();
    const results = await Promise.all(targets.map(async (id) => {
      try {
        const response = await fetch(`${agentBase()}/api/agent/sessions/${id}`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) throw new Error(response.status === 409 ? '会话仍在处理中' : `删除失败（${response.status}）`);
        return { id, ok: true };
      } catch (error) {
        return { id, ok: false, error: error instanceof Error ? error.message : '删除失败' };
      }
    }));
    const removed = new Set(results.filter((item) => item.ok).map((item) => item.id));
    const remaining = sessions.filter((item) => !removed.has(item.id));
    setSessions(remaining);
    setDeletingSessionIds([]);
    setDeleteCandidateId('');
    setConfirmEmptyCleanup(false);
    const failure = results.find((item) => !item.ok);
    if (activeDeleted && removed.has(sessionRef.current || '')) {
      sessionRef.current = undefined;
      setActiveSessionId('');
      setHistory([]);
      rememberSession();
      resetActivity();
      if (remaining[0]) await activateSession(remaining[0]);
      else setStatus('新会话');
    } else if (failure) {
      setStatus(failure.error || '部分会话删除失败');
    } else {
      setStatus(removed.size > 1 ? `已清理 ${removed.size} 个空会话` : '会话已删除');
    }
  };

  const ensureSession = async () => {
    if (sessionRef.current) {
      if (eventsRef.current?.readyState !== EventSource.OPEN) await connect(sessionRef.current);
      return sessionRef.current;
    }
    const response = await fetch(`${agentBase()}/api/agent/sessions`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app_view: { app: 'caigen-web', view: surface, route: window.location.pathname } }),
    });
    if (!response.ok) throw new ApiError(response.status, response.status === 401 ? '请先登录菜根账户' : `Agent 服务不可用（${response.status}）`);
    const payload = await response.json();
    const sessionId = payload.id || payload.session_id;
    sessionRef.current = sessionId;
    setActiveSessionId(sessionId);
    rememberSession(sessionId);
    setSessions((current) => [{ ...(payload as AgentSession), message_count: 0 }, ...current.filter((item) => item.id !== sessionId)]);
    await connect(sessionId);
    return sessionId;
  };

  const sendText = async (rawText: string, carryReply = true) => {
    const text = rawText.trim();
    if (!text || busy) return;
    setHistory((current) => [...current, ...(carryReply && reply ? [{ kind: 'message' as const, role: 'assistant' as const, content: reply }] : []), { kind: 'message', role: 'user', content: text }]);
    setBusy(true); resetActivity();
    setStatus('正在连接菜根助手');
    try {
      const sessionId = await ensureSession();
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionId}/messages`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
      });
      if (!response.ok && response.status !== 202) throw new ApiError(response.status, `发送失败（${response.status}）`);
      setSessions((items) => items.map((item) => item.id === sessionId ? { ...item, message_count: Math.max(1, item.message_count || 0) } : item));
      setInput(''); setStatus('已收到问题，正在分析');
      const current = sessions.find((item) => item.id === sessionId);
      if (!current?.title) {
        const title = text.slice(0, 28);
        void fetch(`${agentBase()}/api/agent/sessions/${sessionId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }) })
          .then((renameResponse) => renameResponse.ok ? renameResponse.json() : null)
          .then((updated) => { if (updated) setSessions((items) => items.map((item) => item.id === sessionId ? { ...updated, message_count: Math.max(1, item.message_count || 0) } : item)); })
          .catch(() => undefined);
      }
    } catch (error) {
      setBusy(false); setStatus(error instanceof Error ? error.message : 'Agent 服务不可用');
    }
  };

  const send = async (event?: FormEvent) => {
    event?.preventDefault();
    await sendText(input);
  };

  const rewindMessage = async (item: HistoryMessage, regenerate = false) => {
    if (!sessionRef.current || item.seq === undefined || busy || rewindingSeq !== undefined) return;
    setRewindingSeq(item.seq); setStatus(regenerate ? '正在重新生成' : '正在撤回消息');
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionRef.current}/rewind`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seq: item.seq }),
      });
      if (!response.ok) throw new Error(`撤回失败（${response.status}）`);
      const payload = await response.json();
      resetActivity();
      setHistory(parseHistoryMessages(payload.messages));
      setInput(item.content);
      setStatus(regenerate ? '已撤回原回答，正在重新生成' : '已撤回，修改后可重新发送');
      window.requestAnimationFrame(() => { composerRef.current?.focus(); composerRef.current?.setSelectionRange(item.content.length, item.content.length); });
      if (regenerate) await sendText(item.content, false);
    } catch (error) { setStatus(error instanceof Error ? error.message : '撤回失败'); }
    finally { setRewindingSeq(undefined); }
  };

  const regenerateLatest = async () => {
    if (!sessionRef.current || busy) return;
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionRef.current}/messages?limit=200`, { credentials: 'include' });
      if (!response.ok) throw new Error(`会话历史加载失败（${response.status}）`);
      const payload = await response.json();
      const user = parseHistoryMessages(payload.messages).filter((item): item is HistoryMessage => item.kind === 'message' && item.role === 'user' && item.seq !== undefined).at(-1);
      if (!user) throw new Error('没有可重新生成的问题');
      await rewindMessage(user, true);
    } catch (error) { setStatus(error instanceof Error ? error.message : '重新生成失败'); }
  };

  const cancel = async () => {
    if (!busy || !sessionRef.current) return;
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionRef.current}/cancel`, { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error(`中断失败（${response.status}）`);
      completionPendingRef.current = false;
      replyQueueRef.current = '';
      setBusy(false); setStatus('已停止本轮处理');
    } catch (error) { setStatus(error instanceof Error ? error.message : '中断失败'); }
  };

  const confirm = async (item: Confirmation, ok: boolean) => {
    if (!sessionRef.current || item.decision) return;
    setConfirmations((current) => current.map((entry) => entry.id === item.id ? { ...entry, decision: ok ? 'accepted' : 'rejected' } : entry));
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${sessionRef.current}/confirm`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tool_id: item.id, ok }),
      });
      if (!response.ok) throw new Error(`确认失败（${response.status}）`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : '确认失败');
      setConfirmations((current) => current.map((entry) => entry.id === item.id ? { ...entry, decision: undefined } : entry));
    }
  };

  const copyAnswer = async (text: string) => {
    try {
      await navigator.clipboard.writeText(safeAgentText(text));
      setStatus('回答已复制');
    } catch { setStatus('复制失败，请手动选择文本'); }
  };

  const exportConversation = () => {
    const lines = history.flatMap((item) => item.kind === 'message' ? [`## ${item.role === 'user' ? '你' : '菜根助手'}`, safeAgentText(item.content), ''] : []);
    if (reply) lines.push('## 菜根助手', safeAgentText(reply), '');
    const blob = new Blob([`# ${sessionTitle(activeSession?.title)}\n\n${lines.join('\n')}`], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sessionTitle(activeSession?.title).replace(/[\\/:*?"<>|]/g, '-')}.md`;
    link.click();
    URL.revokeObjectURL(url);
    setStatus('会话已导出');
  };

  const beginTitleEdit = () => {
    if (!activeSession || busy) return;
    setTitleDraft(sessionTitle(activeSession.title));
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    const title = titleDraft.trim().slice(0, 48);
    if (!activeSession || !title) { setEditingTitle(false); return; }
    try {
      const response = await fetch(`${agentBase()}/api/agent/sessions/${activeSession.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }) });
      if (!response.ok) throw new Error(`重命名失败（${response.status}）`);
      const updated = await response.json();
      setSessions((items) => items.map((item) => item.id === activeSession.id ? { ...item, ...updated, title } : item));
      setStatus('会话名称已保存');
    } catch (error) { setStatus(error instanceof Error ? error.message : '重命名失败'); }
    finally { setEditingTitle(false); }
  };

  const suggestions = ['帮我梳理今天最重要的事情', '看看这周的日程和 DDL', '最近有哪些和我有关的通知'];
  const hasActivity = historyLoading || history.length || reply || progress.length || plan.length || tools.length || confirmations.length || renders.length;
  const activeSession = sessions.find((item) => item.id === activeSessionId);
  const emptySessions = sessions.filter((item) => Number(item.message_count || 0) === 0);

  return <div className={`agent-conversation${spacious ? ' agent-conversation--spacious' : ''}${sessionPanelOpen ? ' sessions-open' : ''}`} onMouseDown={(event) => { if (sessionPanelOpen && !(event.target as HTMLElement).closest('.agent-session-panel,.agent-session-actions')) setSessionPanelOpen(false); }}>
    <div className="agent-intro"><img src="/assets/brand/caigen-root-mark.svg?v=2" alt="" /><div className="agent-intro-copy">{editingTitle ? <form className="agent-title-form" onSubmit={(event) => { event.preventDefault(); void saveTitle(); }}><input autoFocus value={titleDraft} maxLength={48} onChange={(event) => setTitleDraft(event.target.value)} onBlur={() => void saveTitle()} aria-label="会话名称" /></form> : <button className="agent-title-button" type="button" title={activeSession ? '重命名会话' : undefined} onClick={beginTitleEdit}>{activeSession?.title || '菜根助手'}{activeSession ? <Icon name="edit" /> : null}</button>}<span>{safeAgentText(status)}</span></div><div className="agent-session-actions"><button type="button" aria-label="导出当前会话" title="导出当前会话" disabled={!history.length && !reply} onClick={exportConversation}><Icon name="download" /><span>导出</span></button><button type="button" aria-expanded={sessionPanelOpen} onClick={() => setSessionPanelOpen((open) => !open)}>会话 {sessions.length || ''}</button><button type="button" disabled={busy} onClick={newSession}>＋ 新建</button></div></div>
    {sessionPanelOpen ? <section className="agent-session-panel" aria-label="历史会话"><header><div><span>对话目录</span><small>{sessions.length} 个会话</small></div><div className="agent-session-panel-actions">{emptySessions.length ? confirmEmptyCleanup ? <><button type="button" onClick={() => setConfirmEmptyCleanup(false)}>取消</button><button className="danger" type="button" disabled={Boolean(deletingSessionIds.length)} onClick={() => void deleteSessions(emptySessions.map((item) => item.id))}>清理 {emptySessions.length} 个</button></> : <button type="button" disabled={busy} onClick={() => setConfirmEmptyCleanup(true)}>清理空会话</button> : null}<button type="button" onClick={() => setSessionPanelOpen(false)}>收起</button></div></header>{sessions.length ? <div className="agent-session-list">{sessions.map((session) => <article className={`${session.id === activeSessionId ? 'active ' : ''}${deleteCandidateId === session.id ? 'confirming' : ''}`} key={session.id}><button type="button" className="agent-session-open" disabled={busy || deletingSessionIds.includes(session.id)} onClick={() => void activateSession(session)}><span>{sessionTitle(session.title)}</span><small>{session.message_count ? `${session.message_count} 条记录 · ` : '空会话 · '}{sessionTime(session.updated_at || session.created_at)}</small></button>{deleteCandidateId === session.id ? <div className="agent-session-confirm"><span>删除此会话及其记录？</span><button type="button" onClick={() => setDeleteCandidateId('')}>取消</button><button className="danger" type="button" disabled={deletingSessionIds.includes(session.id)} onClick={() => void deleteSessions([session.id])}>删除</button></div> : <button type="button" className="agent-session-delete" aria-label={`删除会话：${sessionTitle(session.title)}`} title="删除会话" disabled={busy} onClick={() => setDeleteCandidateId(session.id)}>删除</button>}</article>)}</div> : <p>还没有历史会话，发送第一条消息后会在这里留下书签。</p>}</section> : null}
    {!hasActivity && <div className="agent-suggestions">{suggestions.map((item) => <button key={item} type="button" onClick={() => setInput(item)}>{item}</button>)}</div>}
    {hasActivity ? <div className="agent-stream" aria-live="polite">
      {historyLoading ? <p className="agent-history-loading">正在翻阅会话记录…</p> : null}
      {history.map((item, index) => item.kind === 'tool'
        ? <article className={`agent-tool agent-tool--${item.state}`} key={`${item.id}-${index}`}><header><span>{item.state === 'ok' ? '完成' : item.state === 'error' ? '失败' : '已调用'}</span><b>{toolNames[item.tool] || item.tool}</b></header>{item.summary ? <p>{item.summary}</p> : null}</article>
        : <article className={`agent-history-message ${item.role}`} key={`${item.run_id || 'message'}-${item.seq ?? index}`}>{item.role === 'assistant' ? <RichText text={safeAgentText(item.content)} /> : <p>{item.content}</p>}{item.seq !== undefined ? <div className="agent-message-actions">{item.role === 'user' ? <button type="button" disabled={busy || rewindingSeq !== undefined} onClick={() => void rewindMessage(item)}><Icon name="edit" />撤回并编辑</button> : <><button type="button" onClick={() => void copyAnswer(item.content)}><Icon name="copy" />复制</button>{(() => { const previous = history.slice(0, index).filter((entry): entry is HistoryMessage => entry.kind === 'message' && entry.role === 'user' && entry.seq !== undefined).at(-1); return previous ? <button type="button" disabled={busy || rewindingSeq !== undefined} onClick={() => void rewindMessage(previous, true)}><Icon name="retry" />重新生成</button> : null; })()}</>}</div> : null}</article>)}
      {plan.length > 1 && <details className="agent-plan"><summary>执行计划 · {plan.length} 步</summary>{plan.map((step, index) => <p key={index}><span>{index + 1}</span>{step.label || toolNames[String(step.tool)] || step.tool}</p>)}</details>}
      {progress.length > 0 && <details className="agent-thinking" open={busy}><summary>处理过程 · {progress.length} 步</summary>{progress.map((item) => <p key={item.id}><span>{progressStages[item.stage] || item.stage}</span>{item.label}</p>)}</details>}
      {tools.map((item) => <article className={`agent-tool agent-tool--${item.state}`} key={item.id}><header><span>{item.state === 'ok' ? '完成' : item.state === 'error' ? '失败' : item.state === 'waiting' ? '待确认' : '运行中'}</span><b>{toolNames[item.tool] || item.tool}</b>{item.duration ? <small>{item.duration}ms</small> : null}</header>{item.summary && <p>{item.summary}</p>}</article>)}
      {confirmations.map((item) => <article className="agent-confirm" key={item.id}><small>ACTION · 需要你的确认</small><h4>{toolNames[item.tool] || item.tool}</h4><p>{item.reason}</p><code>{JSON.stringify(item.args)}</code><div><button disabled={Boolean(item.decision)} onClick={() => confirm(item, false)} type="button">取消</button><button disabled={Boolean(item.decision)} onClick={() => confirm(item, true)} type="button">{item.decision === 'accepted' ? '已确认' : item.decision === 'rejected' ? '已取消' : '确认执行'}</button></div></article>)}
      {renders.map((item) => <RenderCard item={item} key={item.id} />)}
      {reply && <article className="agent-reply"><RichText text={safeAgentText(reply)} />{!busy ? <div className="agent-message-actions"><button type="button" onClick={() => void copyAnswer(reply)}><Icon name="copy" />复制</button><button type="button" disabled={rewindingSeq !== undefined} onClick={() => void regenerateLatest()}><Icon name="retry" />重新生成</button></div> : null}</article>}
    </div> : null}
    <form className="agent-composer" onSubmit={send}><textarea ref={composerRef} value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(); } }} rows={spacious ? 3 : 2} placeholder="问问日程、通知、学习与最近的状态…" /><div className="agent-composer-actions">{busy ? <button className="cancel" type="button" onClick={cancel}>停止</button> : null}<button type="submit" disabled={busy || !input.trim()}>{busy ? '处理中' : '发送'}</button></div></form>
    <p className="agent-boundary">建议附带来源；写入日程前必须由你确认。</p>
  </div>;
}
